import React, { useReducer, Dispatch, JSX } from 'react';
import { createContext, useContext, useContextSelector } from 'use-context-selector';
import { PayloadAction } from 'typesafe-actions';
import { formatDate } from '../../utils/utils';

interface defaultStateInterface {
  user: {
    security: boolean;
  };
  header: {
    notifications: {
      backorders: number;
      shipping: number;
    };
  };
  drawers: {
    drawerMenu: boolean;
    drawerNotifications: boolean;
    drawerCustomer: boolean;
    drawerCustomersFilters: boolean;
    drawerCustomerOrdersFilters: boolean;
    drawerOrder: boolean;
    drawerOrdersFilters: boolean;
    drawerProductsFilters: boolean;
    drawerSupplier: boolean;
    drawerSupplierFilters: boolean;
    drawerSupplierProductsFilters: boolean;
    drawerTransactionsFilters: boolean;
    drawerUser: boolean;
  };
  dialogs: {
    dialogProduct: boolean;
    dialogCustomer: boolean;
    dialogTransaction: boolean;
    dialogTransactionChart: {
      open: boolean;
      data: any;
    };
    dialogDelete: {
      open: boolean;
      title: string;
      onConfirm: () => void;
    };
  };
  tabs: {
    tabsOrder: 'form' | 'products';
    tabsCustomer: 'form' | 'orders' | 'transactions';
    tabsSupplier: 'form' | 'products' | 'transactions';
    tabsUser: 'form' | 'orders' | 'transactions';
    tabsSettings: 'acl' | 'users';
    tabsDashboard: 'new' | 'ready' | 'unpaid' | 'stats';
  };
  forms: {
    formOrder: string;
    formOrderCustomer: string;
    formCustomer: string;
    formCustomerPhone: string;
    formSupplier: string;
    formProduct: string;
    formTransaction: string;
    formTransactionNew: string;
    formUser: string;
  };
  filters: {
    filtersCustomer: {
      customerRef: string;
      sortingField: string;
      sortingOrder: string;
      unpaid: boolean;
      columns: {
        surname: boolean;
        name: boolean;
        phone: boolean;
        email: boolean;
        taxCode: boolean;
        dateCreated: boolean;
        userCreated: boolean;
        dateUpdated: boolean;
        userUpdated: boolean;
        sequenceTotal: boolean;
      };
    };
    filtersCustomerOrders: {
      orderStatus: string;
      dateCreated: string;
      sortingField: string;
      sortingOrder: string;
      columns: {
        dateCreated: boolean;
        userCreated: boolean;
        dateUpdated: boolean;
        userUpdated: boolean;
        dateCompleted: boolean;
        userCompleted: boolean;
        orderStatus: boolean;
        orderTransaction: boolean;
        products: boolean;
        total: boolean;
        downPayment: boolean;
        comments: boolean;
      };
    };
    filtersOrder: {
      customerRef: string;
      orderStatus: string;
      dateCreated: string;
      sortingField: string;
      sortingOrder: string;
      columns: {
        customerRef: boolean;
        comments: boolean;
        dateCreated: boolean;
        userCreated: boolean;
        dateUpdated: boolean;
        userUpdated: boolean;
        dateCompleted: boolean;
        userCompleted: boolean;
        orderStatus: boolean;
        products: boolean;
        total: boolean;
        downPayment: boolean;
      };
    };
    filtersProducts: {
      supplierRef: string;
      productStatus: string;
      dateCreated: string;
      sortingField: string;
      sortingOrder: string;
      columns: {
        supplierRef: boolean;
        productStatus: boolean;
        description: boolean;
        dateCreated: boolean;
        dateUpdated: boolean;
        dateOrdered: boolean;
        dateBackorder: boolean;
        dateReceived: boolean;
        userCreated: boolean;
        userBackorder: boolean;
        userUpdated: boolean;
        userOrdered: boolean;
        userReceived: boolean;
        price: boolean;
      };
    };
    filtersSupplierProducts: {
      dateCreated: string;
      dateOrdered: string;
      dateReceived: string;
      productStatus: string;
      sortingField: string;
      sortingOrder: string;
      columns: {
        description: boolean;
        dateCreated: boolean;
        dateOrdered: boolean;
        dateReceived: boolean;
        productStatus: boolean;
        price: boolean;
        imageUrl: boolean;
        dateUpdated: boolean;
        dateBackorder: boolean;
        userCreated: boolean;
        userOrdered: boolean;
        userReceived: boolean;
        userUpdated: boolean;
        userBackorder: boolean;
      };
    };
    filtersSupplier: {
      supplierRef: string;
      productStatus: string;
      sortingField: string;
      sortingOrder: string;
      columns: {
        name: boolean;
        phone: boolean;
        email: boolean;
        taxCode: boolean;
        comments: boolean;
        website: boolean;
        contactPerson: boolean;
        // productStatus: boolean;
        productStatusList: boolean;
        downPayment: boolean;
        datePayment: boolean;
        sequenceTotal: boolean;
        dateCreated: boolean;
        userCreated: boolean;
        dateUpdated: boolean;
        userUpdated: boolean;
      };
    };
    filtersTransactions: {
      customerRef: string;
      supplierRef: string;
      userRef: string;
      dateStart: string;
      dateEnd: string;
      transactionStatus: string;
      counterStatus: string;
      personalStatus: string;
      sortingField: string;
      sortingOrder: string;
    };
  };
  snackbar: {
    open: boolean;
    severity: string;
    message: string;
  };
}

export const defaultState: defaultStateInterface = {
  user: {
    security: false,
  },
  header: {
    notifications: {
      backorders: 0,
      shipping: 0,
    },
  },
  drawers: {
    drawerMenu: false,
    drawerNotifications: false,
    drawerCustomer: false,
    drawerCustomersFilters: false,
    drawerCustomerOrdersFilters: false,
    drawerOrder: false,
    drawerOrdersFilters: false,
    drawerProductsFilters: false,
    drawerSupplier: false,
    drawerSupplierFilters: false,
    drawerSupplierProductsFilters: false,
    drawerTransactionsFilters: false,
    drawerUser: false,
  },
  dialogs: {
    dialogProduct: false,
    dialogCustomer: false,
    dialogTransaction: false,
    dialogTransactionChart: {
      open: false,
      data: {},
    },
    dialogDelete: {
      open: false,
      title: 'Are you sure you want to delete this customer?',
      onConfirm: () => {},
    },
  },
  tabs: {
    tabsOrder: 'form',
    tabsCustomer: 'form',
    tabsSupplier: 'form',
    tabsUser: 'form',
    tabsSettings: 'users',
    tabsDashboard: 'stats',
  },
  forms: {
    formOrder: '',
    formOrderCustomer: '',
    formCustomer: '',
    formCustomerPhone: '',
    formSupplier: '',
    formProduct: '',
    formTransaction: '',
    formTransactionNew: '',
    formUser: '',
  },
  filters: {
    filtersCustomer: {
      customerRef: '',
      sortingField: 'surname',
      sortingOrder: 'asc',
      unpaid: false,
      columns: {
        name: true,
        surname: true,
        phone: true,
        email: true,
        taxCode: false,
        dateCreated: false,
        userCreated: false,
        dateUpdated: false,
        userUpdated: false,
        sequenceTotal: true,
      },
    },
    filtersCustomerOrders: {
      orderStatus: '',
      dateCreated: '',
      sortingField: 'dateCreated',
      sortingOrder: 'desc',
      columns: {
        dateCreated: true,
        userCreated: true,
        dateUpdated: true,
        userUpdated: true,
        dateCompleted: true,
        userCompleted: true,
        orderStatus: true,
        products: true,
        orderTransaction: true,
        total: true,
        downPayment: true,
        comments: true,
      },
    },
    filtersProducts: {
      supplierRef: '',
      productStatus: '',
      dateCreated: '',
      sortingField: 'dateCreated',
      sortingOrder: 'desc',
      columns: {
        supplierRef: true,
        productStatus: true,
        description: true,
        dateCreated: true,
        dateUpdated: false,
        dateOrdered: false,
        dateBackorder: false,
        dateReceived: false,
        userCreated: false,
        userBackorder: false,
        userUpdated: false,
        userOrdered: false,
        userReceived: false,
        price: true,
      },
    },
    filtersOrder: {
      customerRef: '',
      orderStatus: '',
      dateCreated: '',
      sortingField: 'dateUpdated',
      sortingOrder: 'desc',
      columns: {
        customerRef: true,
        comments: false,
        dateCreated: true,
        userCreated: false,
        dateUpdated: true,
        userUpdated: false,
        dateCompleted: false,
        userCompleted: false,
        orderStatus: true,
        products: true,
        total: true,
        downPayment: true,
      },
    },
    filtersSupplier: {
      supplierRef: '',
      productStatus: '',
      sortingField: 'name',
      sortingOrder: 'asc',
      columns: {
        name: true,
        phone: true,
        email: true,
        taxCode: false,
        comments: false,
        website: false,
        contactPerson: false,
        // productStatus: true,
        productStatusList: true,
        downPayment: true,
        datePayment: true,
        sequenceTotal: true,
        dateCreated: false,
        userCreated: false,
        dateUpdated: false,
        userUpdated: false,
      },
    },
    filtersSupplierProducts: {
      productStatus: '',
      dateCreated: '',
      dateOrdered: '',
      dateReceived: '',
      sortingField: 'dateCreated',
      sortingOrder: 'desc',
      columns: {
        description: true,
        dateCreated: true,
        dateOrdered: true,
        dateReceived: true,
        productStatus: true,
        price: true,
        imageUrl: false,
        dateUpdated: false,
        dateBackorder: false,
        userCreated: false,
        userOrdered: false,
        userReceived: false,
        userUpdated: false,
        userBackorder: false,
      },
    },
    filtersTransactions: {
      customerRef: '',
      supplierRef: '',
      userRef: '',
      dateStart: formatDate(new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1)),
      dateEnd: '',
      transactionStatus: '',
      counterStatus: '',
      personalStatus: '',
      sortingField: 'dateCreated',
      sortingOrder: 'desc',
    },
  },
  snackbar: {
    open: false,
    severity: 'success',
    message: '',
  },
};

export enum Actions {
  User = '@@STATE/USER',
  Header = '@@STATE/HEADER',
  HeaderNotifications = '@@STATE/HEADER_NOTIFICATIONS',
  Forms = '@@STATE/FORMS',
  FormOrder = '@@STATE/FORMS/FORM_ORDER',
  FormUser = '@@STATE/FORMS/FORM_USER',
  FormOrderCustomer = '@@STATE/FORMS/FORM_ORDER_CUSTOMER',
  FormCustomer = '@@STATE/FORMS/FORM_CUSTOMER',
  FormCustomerPhone = '@@STATE/FORMS/FORM_CUSTOMER_PHONE',
  FormSupplier = '@@STATE/FORMS/FORM_SUPPLIER',
  FormProduct = '@@STATE/FORMS/FORM_PRODUCT',
  FormTransaction = '@@STATE/FORMS/FORM_TRANSACTION',
  FormTransactionNew = '@@STATE/FORMS/FORM_TRANSACTION_NEW',
  FiltersOrder = '@@STATE/FILTERS/FILTERS_ORDER',
  FiltersProducts = '@@STATE/FILTERS/FILTERS_PRODUCTS',
  TabsOrder = '@@STATE/TABS/TABS_ORDER',
  TabsCustomer = '@@STATE/TABS/TABS_CUSTOMER',
  TabsSupplier = '@@STATE/TABS/TABS_SUPPLIER',
  TabsUser = '@@STATE/TABS/TABS_USER',
  TabsSettings = '@@STATE/TABS/TABS_SETTINGS',
  TabsDashboard = '@@STATE/TABS/TABS_DASHBOARD',
  FiltersCustomer = '@@STATE/FILTERS/FILTERS_CUSTOMER',
  FiltersCustomerOrders = '@@STATE/FILTERS/FILTERS_CUSTOMER_ORDERS',
  FiltersSupplier = '@@STATE/FILTERS/FILTERS_SUPPLIER',
  FiltersSupplierProducts = '@@STATE/FILTERS/FILTERS_SUPPLIER_ORDER',
  FiltersTransactions = '@@STATE/FILTERS/FILTERS_TRANSACTIONS',
  DialogProduct = '@@STATE/DIALOG/DIALOG_PRODUCT',
  DialogCustomer = '@@STATE/DIALOG/DIALOG_CUSTOMER',
  DialogTransaction = '@@STATE/DIALOG/DIALOG_TRANSACTION',
  DialogTransactionsChart = '@@STATE/DRAWERS/DIALOG_TRANSACTIONS_CHART',
  DialogDelete = '@@STATE/DIALOG/DIALOG_DELETE',
  DrawerMenu = '@@STATE/DRAWERS/DRAWER_MENU',
  DrawerNotifications = '@@STATE/DRAWERS/DRAWER_NOTIFICATIONS',
  DrawerCustomer = '@@STATE/DRAWERS/DRAWER_CUSTOMER',
  DrawerCustomersFilters = '@@STATE/DRAWERS/DRAWER_CUSTOMERS_FILTERS',
  DrawerCustomerOrdersFilters = '@@STATE/DRAWERS/DRAWER_CUSTOMER_ORDERS_FILTERS',
  DrawerOrder = '@@STATE/DRAWERS/DRAWER_ORDER',
  DrawerOrdersFilters = '@@STATE/DRAWERS/DRAWER_ORDERS_FILTERS',
  DrawerProductsFilters = '@@STATE/DRAWERS/DRAWER_PRODUCTS_FILTERS',
  DrawerSupplier = '@@STATE/DRAWERS/DRAWER_SUPPLIER',
  DrawerSupplierFilters = '@@STATE/DRAWERS/DRAWER_SUPPLIER_FILTERS',
  DrawerSupplierProductsFilters = '@@STATE/DRAWERS/DRAWER_SUPPLIER_PRODUCTS_FILTERS',
  DrawerTransactionsFilters = '@@STATE/DRAWERS/DRAWER_TRANSACTIONS_FILTERS',
  DrawerUser = '@@STATE/DRAWERS/DRAWER_USER',
  Snackbar = '@@STATE/SNACKBAR',
}

export const ActionsReducer = (state: defaultStateInterface, action: PayloadAction<Actions, any>) => {
  // console.log(action, state);
  switch (action.type) {
    case Actions.User:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
        },
      };
    case Actions.Header:
      return {
        ...state,
        header: {
          ...state.header,
          ...action.payload,
        },
      };
    case Actions.HeaderNotifications:
      return {
        ...state,
        header: {
          ...state.header,
          notifications: {
            ...state.header.notifications,
            ...action.payload,
          },
        },
      };
    case Actions.Forms:
      return {
        ...state,
        forms: {
          ...state.forms,
          ...action.payload,
        },
      };
    case Actions.FormOrder:
      return {
        ...state,
        forms: {
          ...state.forms,
          formOrder: action.payload,
        },
      };
    case Actions.FormUser:
      return {
        ...state,
        forms: {
          ...state.forms,
          formUser: action.payload,
        },
      };
    case Actions.FormOrderCustomer:
      return {
        ...state,
        forms: {
          ...state.forms,
          formOrderCustomer: action.payload,
        },
      };
    case Actions.FormCustomer:
      return {
        ...state,
        forms: {
          ...state.forms,
          formCustomer: action.payload,
        },
      };
    case Actions.FormCustomerPhone:
      return {
        ...state,
        forms: {
          ...state.forms,
          formCustomerPhone: action.payload,
        },
      };
    case Actions.FormSupplier:
      return {
        ...state,
        forms: {
          ...state.forms,
          formSupplier: action.payload,
        },
      };
    case Actions.FormProduct:
      return {
        ...state,
        forms: {
          ...state.forms,
          formProduct: action.payload,
        },
      };
    case Actions.FormTransaction:
      return {
        ...state,
        forms: {
          ...state.forms,
          formTransaction: action.payload,
        },
      };
    case Actions.FormTransactionNew:
      return {
        ...state,
        forms: {
          ...state.forms,
          formTransactionNew: action.payload,
        },
      };
    case Actions.TabsOrder:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsOrder: action.payload,
        },
      };
    case Actions.TabsCustomer:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsCustomer: action.payload,
        },
      };
    case Actions.TabsSupplier:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsSupplier: action.payload,
        },
      };
    case Actions.TabsUser:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsUser: action.payload,
        },
      };
    case Actions.TabsSettings:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsSettings: action.payload,
        },
      };
    case Actions.TabsDashboard:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          tabsDashboard: action.payload,
        },
      };
    case Actions.FiltersOrder:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersOrder: action.payload,
        },
      };
    case Actions.FiltersCustomer:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersCustomer: action.payload,
        },
      };
    case Actions.FiltersCustomerOrders:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersCustomerOrders: action.payload,
        },
      };
    case Actions.FiltersProducts:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersProducts: action.payload,
        },
      };
    case Actions.FiltersSupplier:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersSupplier: action.payload,
        },
      };
    case Actions.FiltersSupplierProducts:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersSupplierProducts: action.payload,
        },
      };
    case Actions.FiltersTransactions:
      return {
        ...state,
        filters: {
          ...state.filters,
          filtersTransactions: action.payload,
        },
      };
    case Actions.DialogDelete:
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          dialogDelete: action.payload,
        },
      };
    case Actions.DialogCustomer:
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          dialogCustomer: action.payload,
        },
      };
    case Actions.DialogProduct:
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          dialogProduct: action.payload,
        },
      };
    case Actions.DialogTransaction:
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          dialogTransaction: action.payload,
        },
      };
    case Actions.DialogTransactionsChart:
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          dialogTransactionChart: action.payload,
        },
      };
    case Actions.DrawerMenu:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerMenu: action.payload,
        },
      };
    case Actions.DrawerNotifications:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerNotifications: action.payload,
        },
      };
    case Actions.DrawerOrder:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerOrder: action.payload,
        },
      };
    case Actions.DrawerCustomer:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerCustomer: action.payload,
        },
      };
    case Actions.DrawerCustomersFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerCustomersFilters: action.payload,
        },
      };
    case Actions.DrawerCustomerOrdersFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerCustomerOrdersFilters: action.payload,
        },
      };
    case Actions.DrawerOrdersFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerOrdersFilters: action.payload,
        },
      };
    case Actions.DrawerProductsFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerProductsFilters: action.payload,
        },
      };
    case Actions.DrawerSupplier:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerSupplier: action.payload,
        },
      };
    case Actions.DrawerSupplierFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerSupplierFilters: action.payload,
        },
      };
    case Actions.DrawerSupplierProductsFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerSupplierProductsFilters: action.payload,
        },
      };
    case Actions.DrawerTransactionsFilters:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerTransactionsFilters: action.payload,
        },
      };
    case Actions.DrawerUser:
      return {
        ...state,
        drawers: {
          ...state.drawers,
          drawerUser: action.payload,
        },
      };
    case Actions.Snackbar:
      return {
        ...state,
        snackbar: {
          ...state.snackbar,
          ...action.payload,
        },
      };
    default: {
      return defaultState;
    }
  }
};

export interface StateProviderProps {
  children: JSX.Element;
}

const Context = createContext<defaultStateInterface>(defaultState);
const DispatchContext = createContext<Dispatch<PayloadAction<Actions, any>> | undefined>(undefined);

export const useStateProvider = () => {
  return useContext(Context);
};

export const useStateUser = () => {
  return useContextSelector(Context, (state) => state.user);
};

export const useStateHeader = () => {
  return useContextSelector(Context, (state) => state.header);
};

export const useStateFormOrder = () => {
  return useContextSelector(Context, (state) => state.forms.formOrder);
};

export const useStateFormOrderCustomer = () => {
  return useContextSelector(Context, (state) => state.forms.formOrderCustomer);
};

export const useStateFormCustomer = () => {
  return useContextSelector(Context, (state) => state.forms.formCustomer);
};

export const useStateFormCustomerPhone = () => {
  return useContextSelector(Context, (state) => state.forms.formCustomerPhone);
};

export const useStateFormSupplier = () => {
  return useContextSelector(Context, (state) => state.forms.formSupplier);
};

export const useStateFormProduct = () => {
  return useContextSelector(Context, (state) => state.forms.formProduct);
};

export const useStateFormUser = () => {
  return useContextSelector(Context, (state) => state.forms.formUser);
};

export const useStateFormTransaction = () => {
  return useContextSelector(Context, (state) => state.forms.formTransaction);
};

export const useStateFormTransactionNew = () => {
  return useContextSelector(Context, (state) => state.forms.formTransactionNew);
};

export const useStateTabsOrder = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsOrder);
};

export const useStateTabsCustomer = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsCustomer);
};

export const useStateTabsSupplier = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsSupplier);
};

export const useStateTabsSettings = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsSettings);
};

export const useStateTabsUsers = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsUser);
};

export const useStateTabsDashboard = () => {
  return useContextSelector(Context, (state) => state.tabs.tabsDashboard);
};

export const useStateFiltersOrder = () => {
  return useContextSelector(Context, (state) => state.filters.filtersOrder);
};

export const useStateFiltersProducts = () => {
  return useContextSelector(Context, (state) => state.filters.filtersProducts);
};

export const useStateFiltersCustomer = () => {
  return useContextSelector(Context, (state) => state.filters.filtersCustomer);
};

export const useStateFiltersCustomerOrders = () => {
  return useContextSelector(Context, (state) => state.filters.filtersCustomerOrders);
};

export const useStateFiltersSupplierProducts = () => {
  return useContextSelector(Context, (state) => state.filters.filtersSupplierProducts);
};

export const useStateFiltersSupplier = () => {
  return useContextSelector(Context, (state) => state.filters.filtersSupplier);
};

export const useStateFiltersTransactions = () => {
  return useContextSelector(Context, (state) => state.filters.filtersTransactions);
};

export const useStateDialogDelete = () => {
  return useContextSelector(Context, (state) => state.dialogs.dialogDelete);
};

export const useStateDialogProduct = () => {
  return useContextSelector(Context, (state) => state.dialogs.dialogProduct);
};

export const useStateDialogCustomer = () => {
  return useContextSelector(Context, (state) => state.dialogs.dialogCustomer);
};

export const useStateDialogTransaction = () => {
  return useContextSelector(Context, (state) => state.dialogs.dialogTransaction);
};

export const useStateDialogTransactionsChart = () => {
  return useContextSelector(Context, (state) => state.dialogs.dialogTransactionChart);
};

export const useStateDrawerMenu = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerMenu);
};

export const useStateDrawerNotifications = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerNotifications);
};

export const useStateDrawerCustomer = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerCustomer);
};

export const useStateDrawerCustomersFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerCustomersFilters);
};

export const useStateDrawerCustomerOrdersFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerCustomerOrdersFilters);
};

export const useStateDrawerOrder = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerOrder);
};

export const useStateDrawerOrdersFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerOrdersFilters);
};

export const useStateDrawerProductsFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerProductsFilters);
};

export const useStateDrawerSupplier = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerSupplier);
};

export const useStateDrawerSupplierProductsFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerSupplierProductsFilters);
};

export const useStateDrawerSupplierFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerSupplierFilters);
};

export const useStateDrawerTransactionsFilters = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerTransactionsFilters);
};

export const useStateDrawerUser = () => {
  return useContextSelector(Context, (state) => state.drawers.drawerUser);
};

export const useStateSnackbar = () => {
  return useContextSelector(Context, (state) => state.snackbar);
};

export const useDispatchProvider = (): Dispatch<PayloadAction<Actions, any>> | undefined => {
  return useContext(DispatchContext);
};

const StateProvider: React.FC<StateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(ActionsReducer, defaultState);

  return (
    <Context.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </Context.Provider>
  );
};

export default StateProvider;
